import { useMemo } from 'react';
import {
  API_VARIANT_NIST,
  API_VARIANT_MINEX,
  PAGE_OP_MATCH,
  PAGE_OP_QUALITY,
  PAGE_OP_EXTRACT,
  normalizeApiVariant,
  resolveBiosdkRouteUrl,
  resolveVariantStatus,
  resolveNistRouteUrl,
  resolveNistVariantStatus,
  resolveMinexRouteUrl,
  resolveMinexVariantStatus,
  resolveInputTypeLabel,
  buildRequestSummary,
} from '../biosdkRoutes';

function resolvePageOperation(operationPath) {
  const op = String(operationPath || '').toLowerCase();
  if (op === 'match') return PAGE_OP_MATCH;
  if (op === 'process' || op.includes('quality') || op === 'check-quality') return PAGE_OP_QUALITY;
  if (op.includes('extract')) return PAGE_OP_EXTRACT;
  return PAGE_OP_MATCH;
}

export function useBiosdkRoute(
  biosdkBaseUrl,
  operationPath,
  fileExtensions,
  apiVariant,
  contextKey,
  modality,
  pageOperationOverride = null,
) {
  const pageOperation = pageOperationOverride ?? resolvePageOperation(operationPath);
  const isNist = normalizeApiVariant(apiVariant) === API_VARIANT_NIST;
  const isMinex = normalizeApiVariant(apiVariant) === API_VARIANT_MINEX;

  const route = useMemo(() => {
    if (isNist && modality) {
      return resolveNistRouteUrl(biosdkBaseUrl, modality, pageOperation);
    }
    if (isMinex && modality) {
      return resolveMinexRouteUrl(biosdkBaseUrl, modality, pageOperation);
    }
    return resolveBiosdkRouteUrl(biosdkBaseUrl, operationPath, fileExtensions, apiVariant, contextKey);
  }, [biosdkBaseUrl, operationPath, fileExtensions, apiVariant, contextKey, isNist, isMinex, modality, pageOperation]);

  const variantStatus = useMemo(() => {
    if (isNist && modality) {
      return resolveNistVariantStatus(
        biosdkBaseUrl,
        modality,
        pageOperation,
        fileExtensions,
        contextKey
      );
    }
    if (isMinex && modality) {
      return resolveMinexVariantStatus(
        biosdkBaseUrl,
        modality,
        pageOperation,
        fileExtensions,
        contextKey
      );
    }
    return resolveVariantStatus(apiVariant, fileExtensions, operationPath, biosdkBaseUrl, contextKey);
  }, [apiVariant, fileExtensions, operationPath, biosdkBaseUrl, contextKey, isNist, isMinex, modality, pageOperation]);

  const inputTypeLabel = useMemo(
    () => resolveInputTypeLabel(fileExtensions, apiVariant, contextKey),
    [fileExtensions, apiVariant, contextKey]
  );

  const makeRequestSummary = (operation, inputs, extra = {}) =>
    buildRequestSummary({ variantStatus, inputTypeLabel, operation, inputs, extra });

  return { route, variantStatus, inputTypeLabel, makeRequestSummary };
}
